import { useState, useRef, useEffect } from "react";
import { Plus, Minus } from "lucide-react";
import { CPT_CODES, getColorClasses, getAccentColor, getBadgeBg } from "@/lib/constants";
import { useTheme } from "@/contexts/ThemeContext";

// Get color classes based on color name and theme
const getColorClassesByName = (color, isDark) => {
  if (isDark) {
    const darkMap = {
      blue: "bg-blue-500/10 text-blue-400 border-blue-500/30 hover:border-blue-400/60",
      emerald: "bg-emerald-500/10 text-emerald-400 border-emerald-500/30 hover:border-emerald-400/60",
      orange: "bg-orange-500/10 text-orange-400 border-orange-500/30 hover:border-orange-400/60",
      red: "bg-red-500/10 text-red-400 border-red-500/30 hover:border-red-400/60",
      yellow: "bg-yellow-400/10 text-yellow-400 border-yellow-400/30 hover:border-yellow-300/60",
      pink: "bg-pink-400/10 text-pink-400 border-pink-400/30 hover:border-pink-300/60",
      rose: "bg-pink-400/10 text-pink-400 border-pink-400/30 hover:border-pink-300/60",
    };
    return darkMap[color] || darkMap.blue;
  } else {
    const lightMap = {
      blue: "bg-blue-50 text-blue-700 border-blue-300 hover:border-blue-500",
      emerald: "bg-emerald-50 text-emerald-700 border-emerald-300 hover:border-emerald-500",
      orange: "bg-orange-50 text-orange-700 border-orange-300 hover:border-orange-500",
      red: "bg-red-50 text-red-700 border-red-300 hover:border-red-500",
      yellow: "bg-yellow-50 text-yellow-700 border-yellow-300 hover:border-yellow-500",
      pink: "bg-pink-50 text-pink-700 border-pink-300 hover:border-pink-500",
      rose: "bg-pink-50 text-pink-700 border-pink-300 hover:border-pink-500",
    };
    return lightMap[color] || lightMap.blue;
  }
};

const getAccentColorByName = (color, isDark) => {
  if (isDark) {
    const darkMap = {
      blue: "text-blue-400",
      emerald: "text-emerald-400",
      orange: "text-orange-400",
      red: "text-red-400",
      yellow: "text-yellow-400",
      pink: "text-pink-400",
      rose: "text-pink-400",
    };
    return darkMap[color] || darkMap.blue;
  } else {
    const lightMap = {
      blue: "text-blue-600",
      emerald: "text-emerald-600",
      orange: "text-orange-600",
      red: "text-red-600",
      yellow: "text-yellow-600",
      pink: "text-pink-600",
      rose: "text-pink-600",
    };
    return lightMap[color] || lightMap.blue;
  }
};

const getBadgeBgByName = (color, isDark) => {
  if (isDark) {
    const darkMap = {
      blue: "bg-blue-500/20",
      emerald: "bg-emerald-500/20",
      orange: "bg-orange-500/20",
      red: "bg-red-500/20",
      yellow: "bg-yellow-400/20",
      pink: "bg-pink-400/20",
      rose: "bg-pink-400/20",
    };
    return darkMap[color] || darkMap.blue;
  } else {
    const lightMap = {
      blue: "bg-blue-100",
      emerald: "bg-emerald-100",
      orange: "bg-orange-100",
      red: "bg-red-100",
      yellow: "bg-yellow-100",
      pink: "bg-pink-100",
      rose: "bg-pink-100",
    };
    return lightMap[color] || lightMap.blue;
  }
};

export default function TallyCard({ code, count, onIncrement, onDecrement, onSetCount, customVisit }) {
  const { theme } = useTheme();
  const isDark = theme === 'dark';
  const [isPulsing, setIsPulsing] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [editValue, setEditValue] = useState("");
  const inputRef = useRef(null);
  const timeoutRef = useRef(null);
  
  // Use customVisit if provided, otherwise fall back to CPT_CODES lookup
  const cpt = customVisit || CPT_CODES.find(c => c.code === code);
  
  // Get styling based on color (from custom visit or default lookup)
  const visitColor = cpt?.color || "blue";
  const colorClasses = getColorClassesByName(visitColor, isDark);
  const accentColor = getAccentColorByName(visitColor, isDark);
  const badgeBg = getBadgeBgByName(visitColor, isDark);

  const handleIncrement = (e) => {
    e.stopPropagation();
    onIncrement(code);
    setIsPulsing(true);
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    timeoutRef.current = setTimeout(() => setIsPulsing(false), 200);
  };

  const handleDecrement = (e) => {
    e.stopPropagation();
    if (count > 0) onDecrement(code);
  };

  const handleCountClick = (e) => {
    e.stopPropagation();
    setEditValue(String(count));
    setIsEditing(true);
  };

  const commitEdit = () => {
    const parsed = parseInt(editValue, 10);
    if (!isNaN(parsed) && parsed >= 0) {
      onSetCount(code, parsed);
    }
    setIsEditing(false);
  };

  const handleEditKeyDown = (e) => {
    if (e.key === "Enter") {
      commitEdit();
    } else if (e.key === "Escape") {
      setIsEditing(false);
    }
  };

  useEffect(() => {
    if (isEditing && inputRef.current) {
      inputRef.current.focus();
      setTimeout(() => {
        if (inputRef.current) inputRef.current.select();
      }, 10);
    }
  }, [isEditing]);

  const renderTallyMarks = () => {
    if (count === 0) return <span className={`text-sm italic ${isDark ? 'text-slate-600' : 'text-gray-400'}`}>
      use + button to tally
    </span>;
    const groups = Math.floor(count / 5);
    const remainder = count % 5;
    const marks = [];
    for (let i = 0; i < groups; i++) {
      marks.push(
        <span key={`g-${i}`} className={`inline-flex mr-2.5 ${accentColor}`}>
          <span className="relative inline-flex items-end gap-[3px]">
            <span className="w-[2px] h-4 bg-current rounded-full" />
            <span className="w-[2px] h-4 bg-current rounded-full" />
            <span className="w-[2px] h-4 bg-current rounded-full" />
            <span className="w-[2px] h-4 bg-current rounded-full" />
            <span className="absolute w-[22px] h-[2px] bg-current rounded-full top-1/2 -translate-y-1/2 -rotate-[35deg] left-[-2px]" />
          </span>
        </span>
      );
    }
    for (let i = 0; i < remainder; i++) {
      marks.push(
        <span key={`r-${i}`} className={`inline-flex mr-[3px] ${accentColor}`}>
          <span className="w-[2px] h-4 bg-current rounded-full" />
        </span>
      );
    }
    return <div className="flex flex-wrap items-end gap-y-2 min-h-[24px]">{marks}</div>;
  };

  return (
    <div
      data-testid={`tally-card-${code}`}
      className={`relative flex flex-col p-5 rounded-xl border-2 select-none ${colorClasses}`}
    >
      <div className="flex items-center justify-between mb-3">
        <span className={`text-2xl md:text-3xl font-bold font-mono ${accentColor}`}>
          {code}
        </span>

        {isEditing ? (
          <input
            ref={inputRef}
            data-testid={`count-input-${code}`}
            type="number"
            min="0"
            value={editValue}
            onChange={(e) => setEditValue(e.target.value)}
            onBlur={commitEdit}
            onKeyDown={handleEditKeyDown}
            className={`w-20 text-3xl md:text-4xl font-mono font-bold text-right bg-transparent border-b-2 border-current outline-none ${accentColor} [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none`}
          />
        ) : (
          <div
            data-testid={`count-display-${code}`}
            onClick={handleCountClick}
            title="Click to edit"
            className={`text-3xl md:text-4xl font-mono font-bold cursor-pointer hover:opacity-80 transition-opacity ${isPulsing ? 'tally-pulse' : ''} ${accentColor}`}
          >
            {count}
          </div>
        )}
      </div>

      <p className={`text-xs mb-3 ${isDark ? 'text-slate-500' : 'text-gray-600'}`}>{cpt?.description}</p>

      <div className="flex-1 mb-4">
        {renderTallyMarks()}
      </div>

      <div className="flex items-center justify-between">
        <span className={`text-xs font-mono ${isDark ? 'text-slate-500' : 'text-gray-600'}`}>
          {cpt?.wrvu > 0 ? `${cpt.wrvu} wRVU ea.` : "0 wRVU"}
        </span>
        <div className="flex items-center gap-6">
          <button
            data-testid={`decrement-${code}`}
            onClick={handleDecrement}
            className={`flex items-center justify-center w-20 h-14 rounded-lg active:scale-95 transition-all border ${
              isDark 
                ? 'bg-red-500/15 hover:bg-red-500/25 active:bg-red-500/35 text-red-400 hover:text-red-300 border-red-500/30 hover:border-red-400/50'
                : 'bg-red-50 hover:bg-red-100 active:bg-red-200 text-red-600 hover:text-red-700 border-red-300 hover:border-red-500'
            }`}
          >
            <Minus size={28} strokeWidth={2.5} />
          </button>
          <button
            data-testid={`increment-${code}`}
            onClick={handleIncrement}
            className={`flex items-center justify-center w-20 h-14 rounded-lg active:scale-95 transition-all border ${
              isDark 
                ? 'bg-emerald-500/15 hover:bg-emerald-500/25 active:bg-emerald-500/35 text-emerald-400 hover:text-emerald-300 border-emerald-500/30 hover:border-emerald-400/50'
                : 'bg-emerald-50 hover:bg-emerald-100 active:bg-emerald-200 text-emerald-600 hover:text-emerald-700 border-emerald-300 hover:border-emerald-500'
            }`}
          >
            <Plus size={28} strokeWidth={2.5} />
          </button>
        </div>
      </div>
    </div>
  );
}
